import { validTypes } from './constants'
import {
  GoogleMapsPlace,
  GoogleMapsStructuredPlace,
  NavigationComponent
} from '../interfaces/googleMapsInterfaces'
import { ESearchLevel } from '../enums'
/**
 * This function is used to get the search level of a place.
 * @param {string} type type of the place
 * @example "administrative_area_level_3"
 * @returns {string} returns the search level of the place
 * @example "municipality"
 * */
const getCoincidenceLevel = (type: string): ESearchLevel => {
  const municipalityTypes = ['administrative_area_level_3', 'locality']

  const neighborhoodTypes = [
    'sublocality_level_1',
    'neighborhood',
    'colloquial_area'
  ]

  const propertyTypes = ['premise', 'street_address', 'establishment']

  if (
    type === 'route' ||
    propertyTypes.some((value: string) => value === type)
  ) {
    return ESearchLevel.PROPERTY
  }

  if (neighborhoodTypes.some((value: string) => value === type)) {
    return ESearchLevel.NEIGHBORHOOD
  }

  if (municipalityTypes.some((value: string) => value === type)) {
    return ESearchLevel.MUNICIPALITY
  }

  return ESearchLevel.STATE
}

/**
 * This function is used to get the street number of a place.
 * @param {string} address address of the place
 * @example "Calle 5 # 3 - 3"
 * @returns {string} returns the street number of the place
 * @example "3"
 * */
function extractCoincidenceStreetNumber(address: string): string {
  const addressParts = address.split(',')

  if (addressParts.length < 1) {
    return ''
  }

  const firstPart = addressParts[0].trim()
  const regex = /\d+/
  const match = regex.exec(firstPart)

  if (match && match.length > 0) {
    return match[0]
  }

  return ''
}

/**
 * This function is used to format the response of the google autocomplete service.
 * @param {GoogleMapsPlace} place place details
 * @param {GoogleMapsPrediction} prediction prediction details
 * @returns {GoogleMapsStructuredPlace} returns the formatted place
 * */
export const formatGoogleAutoCompleteResponse = (params: {
  place: GoogleMapsPlace
  originalfullAddress?: string
}): GoogleMapsStructuredPlace => {
  const { place, originalfullAddress } = params

  const { addressComponents, id, types, location } = place

  const formatted: GoogleMapsStructuredPlace = {
    placeId: id,
    fullAddress: originalfullAddress ?? '',
    streetNumber: '',
    street: '',
    neighborhood: '',
    town: '',
    location: '',
    postalCode: '',
    searchLevel: '',
    coordinates: location,

    navigation: {
      state: '',
      municipality: '',
      neighborhood: '',
      property: ''
    }
  }

  if (types?.length) {
    formatted.searchLevel = getCoincidenceLevel(types[0])
  }

  addressComponents.forEach(({ types, longText }) => {
    if (types.some((type: string) => type === 'street_number')) {
      formatted.streetNumber = longText
    }
    if (formatted.street === '') {
      formatted.streetNumber = extractCoincidenceStreetNumber(
        formatted.fullAddress
      )
    }
    if (types.some((type: string) => type === 'route')) {
      formatted.street = longText
    }
    if (
      types.some(
        (type: string) =>
          type === 'sublocality' || type === 'sublocality_level_1'
      )
    ) {
      formatted.neighborhood = longText
    }
    if (types.some((type: string) => type === 'locality')) {
      formatted.town = longText
    }
    if (types.some((type: string) => type === 'administrative_area_level_1')) {
      formatted.location = longText
    }
    if (types.some((type: string) => type === 'postal_code')) {
      formatted.postalCode = longText
    }
  })

  const addressParts = originalfullAddress?.split(',') ?? []
  formatted.town = addressParts?.[2]

  formatted.navigation = getNavigationByAddress({
    address: formatted.fullAddress,
    searchLevel: formatted.searchLevel,
    notCountry: true
  })

  return formatted
}

/**
 * This function is used to filter the places by types.
 * @param {any[]} places places to filter
 */
export const filterByTypes = (params: { places: any[] }) => {
  const { places } = params

  const result = places?.filter((item: any) => {
    return validTypes.some((type: any) => {
      return item.types.includes(type)
    })
  })

  return result
}

export const getNavigationByAddress = (params: {
  address: string
  searchLevel: string
  notCountry?: boolean
}): NavigationComponent => {
  const { address, searchLevel, notCountry } = params

  const navigation: NavigationComponent = {
    state: '',
    municipality: '',
    neighborhood: '',
    property: ''
  }

  const splitAddress = address.split(',').reverse()

  const baseIndex = notCountry ? 1 : 0
  const municipality = baseIndex + 1
  const neighborhood = baseIndex + 2
  const property = baseIndex + 3

  if (searchLevel === ESearchLevel.STATE) {
    navigation.state = splitAddress[baseIndex]
  }

  if (searchLevel === ESearchLevel.MUNICIPALITY) {
    navigation.state = splitAddress[baseIndex]
    navigation.municipality = splitAddress[municipality]
  }

  if (searchLevel === ESearchLevel.NEIGHBORHOOD) {
    navigation.state = splitAddress[baseIndex]
    navigation.municipality = splitAddress[municipality]
    navigation.neighborhood = splitAddress[neighborhood]
  }

  if (searchLevel === ESearchLevel.PROPERTY) {
    navigation.state = splitAddress[baseIndex]
    navigation.municipality = splitAddress[municipality]
    navigation.neighborhood = splitAddress[neighborhood]
    navigation.property = splitAddress[property]
  }

  return navigation
}
